import { ButtonTertiary } from "@nova/component-btn"
import { TriCheckbox } from "@nova/component-checkbox"
import { Dropdown } from "@nova/component-dropdown"
import * as Icons from "@nova/component-icons"
import { InputOutlined } from "@nova/ds-component-input"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { ButtonSplit } from "@nova/ds-component-button-split"
import { useCallback, useMemo, useState } from "react"
import { useTags } from "../tags/TagQueries"

export type TagFilterValue = { id: string; name: string }[]

interface TagFilterProps {
    value: TagFilterValue
    onChange: (value: TagFilterValue) => void
}

export const TagFilter = ({ value, onChange }: TagFilterProps) => {
    const [search, setSearch] = useState("")

    const { data: tags = [] } = useTags()

    const filtered = useMemo(
        () =>
            tags
                .filter((t) => t.name.toLowerCase().includes(search.toLowerCase()))
                .sort((a, b) => a.name.localeCompare(b.name)),
        [tags, search]
    )

    const handleToggle = useCallback(
        (tag: { id: string; name: string }) => {
            const isSelected = value.some((t) => t.id === tag.id)
            onChange(isSelected ? value.filter((t) => t.id !== tag.id) : [...value, tag])
        },
        [value, onChange]
    )

    const label = value.length === 1 ? value[0].name : `${value.length} tags`

    return (
        <Dropdown>
            <Dropdown.Trigger as="div" className="group">
                {value.length > 0 ? (
                    <ButtonSplit
                        label={label}
                        onRightClick={(e) => {
                            e.stopPropagation()
                            onChange([])
                        }}
                    />
                ) : (
                    <ButtonTertiary
                        title="Tag"
                        RightIcon={Icons.ArrowDropdown}
                        rightIconClassName="transition-transform group-data-[state=open]:rotate-180"
                        className="group-data-[state=open]:!border-gray-300 group-data-[state=open]:!text-gray-0"
                    />
                )}
            </Dropdown.Trigger>
            <Dropdown.Menu className="w-[300px] text-gray-100">
                <div className="p-3 pb-2">
                    <InputOutlined
                        value={search}
                        onChange={(e) => setSearch(e.currentTarget.value)}
                        placeholder="Search for a tag"
                        leftItem={<Icons.Icon Icon={Icons.MagnifyingGlass} className="mr-1" />}
                        autoFocus
                    />
                </div>
                <ScrollableArea className="max-h-[300px]">
                    {filtered.map((tag) => {
                        const isSelected = value.some((t) => t.id === tag.id)
                        return (
                            <Dropdown.Item
                                key={tag.id}
                                clickBehaviour="keep-open"
                                onClick={() => handleToggle(tag)}
                                className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-500 transition-colors"
                            >
                                <span className="text-sm text-white truncate pr-3">{tag.name}</span>
                                <TriCheckbox state={isSelected ? "checked" : "unchecked"} />
                            </Dropdown.Item>
                        )
                    })}
                </ScrollableArea>
            </Dropdown.Menu>
        </Dropdown>
    )
}
